import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import hazardReportService from "../services/hazardReportService";
import plantService from "../services/plantService";
import authService from "../services/authService";
import { toast } from "react-toastify"; // Switched to react-toastify
import {
  HiOutlinePlus,
  HiOutlineMagnifyingGlass,
  HiOutlineCalendarDays,
  HiOutlineChevronDown,
  HiOutlineCog6Tooth,
  HiOutlinePencil,
  HiOutlineTrash,
  HiOutlineArrowRightCircle,
  HiOutlineExclamationTriangle,
  HiOutlineShieldCheck,
  HiOutlineEye,
  HiOutlineChevronRight,
  HiOutlineXMark,
  HiOutlineSparkles,
  HiOutlinePhoto,
  HiOutlineCheckCircle,
  HiOutlineArrowUpTray,
  HiOutlineCpuChip,
} from "react-icons/hi2";
import HazardReportForm from "./HazardReportForm";
import { aiService } from "../services/aiService";
import { SEMANTIC_SUCCESS_MID } from '../theme/chartColors';
import SharedModal from "../components/common/SharedModal";
import PageLoading from "../components/common/PageLoading";
import "./HazardReport.css";

const HazardReportList = () => {
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isAIFeatureOpen, setIsAIFeatureOpen] = useState(false);
  const [editingHazardId, setEditingHazardId] = useState(null);
  const [aiPrefillData, setAiPrefillData] = useState(null);
  const [aiPrefillFiles, setAiPrefillFiles] = useState([]);

  // Approval Modal State
  const [approvalModal, setApprovalModal] = useState({
    isOpen: false,
    hazardId: null,
    status: "",
    remarks: "",
  });

  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    id: null,
    type: "", // 'delete' or 'escalate'
    title: "",
    message: "",
  });

  const [reports, setReports] = useState([]);
  const [plants, setPlants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [expandedGroups, setExpandedGroups] = useState({});

  // Filters
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1;
  const [searchParams] = useSearchParams();

  const [filters, setFilters] = useState({
    year: String(currentYear),
    month: String(currentMonth),
    plantId: "",
    approvalStatus: searchParams.get("approvalStatus") || "",
    status: searchParams.get("status") || "",
    immediateDanger: searchParams.get("immediateDanger") || "",
  });

  const navigate = useNavigate();

  const currentUser = authService.getUser();
  const currentUserRoleId = currentUser ? currentUser.roleId : null;

  const fetchPlants = async () => {
    try {
      const data = await plantService.getAllPlants();
      setPlants(data || []);
      
      // Auto-select plant if only 1 exists
      if (data?.length === 1) {
        setFilters(prev => ({ ...prev, plantId: data[0].plantId.toString() }));
      }
    } catch (err) {
      console.error("Failed to fetch plants:", err);
    }
  };

  const fetchReports = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch all reports (filtering logic can be client-side initially to match user needs)
      const data = await hazardReportService.getHazardReports();
      setReports(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Error fetching hazard reports:", error);
      toast.error("Failed to load hazard reports");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPlants();
    fetchReports();
  }, [fetchReports]);

  const handleDelete = (e, id) => {
    e.stopPropagation();
    setDeleteModal({
      isOpen: true,
      id: id,
      type: "delete",
      title: "Delete Hazard Report",
      message:
        "Are you sure you want to delete this hazard report? This action cannot be undone.",
    });
  };

  const confirmDelete = async () => {
    const { id } = deleteModal;
    try {
      await hazardReportService.deleteHazardReport(id);
      toast.success("Hazard report deleted successfully");
      setDeleteModal({ ...deleteModal, isOpen: false });
      fetchReports();
    } catch (error) {
      console.error("Error deleting report:", error);
      toast.error("Failed to delete report");
    }
  };

  const handleEscalate = (e, id) => {
    e.stopPropagation();
    setDeleteModal({
      isOpen: true,
      id: id,
      type: "escalate",
      title: "Escalate to CAPA",
      message:
        "Are you sure you want to escalate this hazard report to a CAPA? This will trigger an automatic CAPA creation.",
    });
  };

  const confirmEscalate = async () => {
    const { id } = deleteModal;
    try {
      await hazardReportService.escalateToCapa(id);
      toast.success("Hazard escalated to CAPA successfully");
      setDeleteModal({ ...deleteModal, isOpen: false });
      fetchReports();
    } catch (error) {
      console.error("Error escalating report:", error);
      toast.error("Failed to escalate report");
    }
  };

  const submitApproval = async () => {
    if (!approvalModal.remarks.trim()) {
      toast.error("Remarks are required to process approval");
      return;
    }

    try {
      const result = await hazardReportService.approveHazardReport(
        approvalModal.hazardId,
        approvalModal.status,
        approvalModal.remarks,
      );

      let successMessage = `Hazard report ${approvalModal.status.toLowerCase()} successfully`;
      if (
        approvalModal.status === "Approved" &&
        result &&
        result.linkedCapaId
      ) {
        successMessage += ` and CAPA #${result.linkedCapaId} created`;
      }

      toast.success(successMessage);

      setApprovalModal({
        isOpen: false,
        hazardId: null,
        status: "",
        remarks: "",
      });
      fetchReports();
    } catch (error) {
      console.error("Error approving report:", error);
      toast.error(`Failed to ${approvalModal.status.toLowerCase()} report`);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  // Client-side filtering based on filters and search
  const filteredReports = useMemo(() => {
    return reports.filter((report) => {
      const matchesSearch =
        (report.title || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
        (report.location || "")
          .toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        String(report.hazardId).includes(searchTerm);

      const reportDate = new Date(report.reportedAt);
      const matchesYear =
        !filters.year || reportDate.getFullYear() === parseInt(filters.year);
      const matchesMonth =
        !filters.month || reportDate.getMonth() + 1 === parseInt(filters.month);
      const matchesPlant =
        !filters.plantId ||
        (report.plantId || report.plant?.plantId || report.plant?.id) ===
        (filters.plantId.toString().includes("-")
          ? filters.plantId
          : parseInt(filters.plantId));

      const matchesApprovalStatus =
        !filters.approvalStatus || report.approvalStatus === filters.approvalStatus;

      const matchesStatus =
        !filters.status || report.status === filters.status;

      const matchesImmediateDanger =
        !filters.immediateDanger || String(report.isImmediateDanger) === filters.immediateDanger;

      return matchesSearch && matchesYear && matchesMonth && matchesPlant && matchesApprovalStatus && matchesStatus && matchesImmediateDanger;
    });
  }, [reports, searchTerm, filters]);

  const groupedReports = useMemo(() => {
    const groups = {};
    filteredReports.forEach((report) => {
      const date = new Date(report.reportedAt);
      const firstDay = new Date(date.getFullYear(), date.getMonth(), 1);
      const weekNumber = Math.ceil((date.getDate() + firstDay.getDay()) / 7);
      const monthLabel = date.toLocaleDateString("en-GB", {
        month: "long",
        year: "numeric",
      });
      const weekKey = `Week-${weekNumber} ${monthLabel}`;

      if (!groups[weekKey]) groups[weekKey] = {};

      const plantName =
        report.plant?.plantName || report.location || "Unknown Plant";
      const categoryName =
        report.category?.categoryName ||
        report.categoryName ||
        "General Hazard";
      const locationKey = `${plantName} - ${categoryName}`;

      if (!groups[weekKey][locationKey]) {
        groups[weekKey][locationKey] = {
          plant: plantName,
          category: categoryName,
          status: report.status,
          items: [],
          totalHazards: 0,
          immediateDanger: 0,
          pendingCapa: 0,
        };
      }

      const group = groups[weekKey][locationKey];
      group.items.push(report);
      group.totalHazards += 1;
      if (report.isImmediateDanger) group.immediateDanger += 1;
      if (report.status === "Open") group.pendingCapa += 1;

      // Update group status if any item is open
      if (report.status === "Open") group.status = "Open";
    });
    return groups;
  }, [filteredReports]);

  const MONTHS = [
    { value: 1, label: "January" },
    { value: 2, label: "February" },
    { value: 3, label: "March" },
    { value: 4, label: "April" },
    { value: 5, label: "May" },
    { value: 6, label: "June" },
    { value: 7, label: "July" },
    { value: 8, label: "August" },
    { value: 9, label: "September" },
    { value: 10, label: "October" },
    { value: 11, label: "November" },
    { value: 12, label: "December" },
  ];

  const yearOptions = [];
  for (let y = currentYear + 1; y >= currentYear - 2; y--) yearOptions.push(y);

  return (
    <div className="pagecontainer">
      {/* Header */}
      <div className="page-header">
        <div className="header-content">
          <h1>Hazard Reports</h1>
          <p>Manage and track hazard reports across the facility</p>
        </div>
        <div
          className="header-actions"
          style={{ display: "flex", gap: "12px" }}
        >
          <button
            className="btn btn-primary"
            style={{
              background: "linear-gradient(135deg, #a855f7 0%, #7c3aed 100%)",
            }}
            title="AI Image Feature"
            onClick={() => {
              setAiPrefillData(null);
              setAiPrefillFiles([]);
              setIsAIFeatureOpen(true);
            }}
          >
            <HiOutlineSparkles size={20} /> Smart AI Scan
          </button>
          <button
            className="btn btn-primary"
            onClick={() => {
              setEditingHazardId(null);
              setIsFormOpen(true);
            }}
          >
            <HiOutlinePlus size={20} /> Report Hazard
          </button>
        </div>
      </div>

      {/* Filter Bar */}
      <div className="filter-actions-bar-setu">
        <div className="filter-controls-setu">
          <select
            name="plantId"
            value={filters.plantId}
            onChange={handleFilterChange}
            className="filter-select-setu"
          >
            <option value="">Select Plant</option>
            {plants.map((p) => (
              <option key={p.plantId || p.id} value={p.plantId || p.id}>
                {p.plantName}
              </option>
            ))}
          </select>
          <select
            name="year"
            value={filters.year}
            onChange={handleFilterChange}
            className="filter-select-setu"
          >
            <option value="">Select Year</option>
            {yearOptions.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
          <select
            name="month"
            value={filters.month}
            onChange={handleFilterChange}
            className="filter-select-setu"
          >
            <option value="">Select Month</option>
            {MONTHS.map((m) => (
              <option key={m.value} value={m.value}>
                {m.label}
              </option>
            ))}
          </select>
        </div>
        <div className="search-actions-setu">
          <div className="search-wrapper-setu">
            <span className="search-icon-setu">
              <HiOutlineMagnifyingGlass />
            </span>
            <input
              type="text"
              placeholder="Search Here..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input-setu"
            />
          </div>
          <button
            className="settings-btn-setu"
            title="Settings"
            onClick={() => navigate("/home/hazard-reports/settings")}
          >
            <HiOutlineCog6Tooth />
          </button>
        </div>
      </div>

      <div className="count-badge">
        {filteredReports.length}{" "}
        {filteredReports.length === 1 ? "hazard" : "hazards"} found
      </div>

      {/* Content Groups */}
      <div className="report-groups">
        {loading ? (
          <PageLoading variant="compact" message="Loading hazard reports…" />
        ) : filteredReports.length === 0 ? (
          <div className="empty-state">
            <HiOutlineExclamationTriangle className="empty-icon" />
            <h3>No hazard reports found</h3>
            <p>Try adjusting your search or filters</p>
          </div>
        ) : (
          Object.entries(groupedReports).map(([weekKey, locations]) => (
            <div key={weekKey} className="week-group">
              <div className="week-header-simple">{weekKey}</div>
              {Object.entries(locations).map(([locationKey, data]) => {
                const isExpanded = expandedGroups[locationKey];
                const toggleExpand = () =>
                  setExpandedGroups((prev) => ({
                    ...prev,
                    [locationKey]: !isExpanded,
                  }));

                return (
                  <div key={locationKey} className="location-card-container">
                    <div
                      className="location-card-header-v2 cursor-pointer"
                      onClick={toggleExpand}
                    >
                      <div className="header-left-v2">
                        <div className="icon-box-v2 chevron">
                          <HiOutlineChevronDown
                            className={isExpanded ? "" : "collapsed-icon"}
                          />
                        </div>
                        <div className="icon-box-v2 calendar">
                          <HiOutlineCalendarDays />
                        </div>
                        <div className="header-titles-v2">
                          <div className="location-title-v2">{locationKey}</div>
                          <div className="status-v2">
                            Status:{" "}
                            <span
                              className={
                                data.status.toLowerCase() === "open"
                                  ? "open"
                                  : data.status.toLowerCase() === "completed"
                                    ? "completed"
                                    : ""
                              }
                            >
                              {data.status}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="header-right-v2">
                        <div
                          className="btn btn-primary"
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditingHazardId(null);
                            setIsFormOpen(true);
                          }}
                        >
                          <HiOutlinePlus /> Add
                        </div>
                      </div>
                    </div>

                    {isExpanded && (
                      <div className="location-card-body-v2">
                        <table className="exact-pro-table">
                          <thead>
                            <tr>
                              <th>Hazard Number</th>
                              <th className="text-center">Date</th>
                              <th className="text-center">Category</th>
                              <th className="text-center">Urgency</th>
                              <th className="text-center">Approval Status</th>
                              <th className="text-center">Manage</th>
                              <th className="text-center">Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {data.items.map((report) => {
                              const reportedDate = new Date(report.reportedAt);
                              return (
                                <tr key={report.hazardId}>
                                  <td>
                                    <span
                                      className="obs-number-link"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        setEditingHazardId(report.hazardId);
                                        setIsFormOpen(true);
                                      }}
                                    >
                                      <HiOutlineShieldCheck className="obs-icon" />
                                      {report.referenceNumber ||
                                        `HZD-${reportedDate.getFullYear()}-${report.hazardId.toString().padStart(3, "0")}`}
                                    </span>
                                  </td>
                                  <td className="text-center">
                                    {reportedDate.toLocaleDateString("en-GB")}
                                  </td>
                                  <td className="text-center">
                                    {report.category?.categoryName ||
                                      report.categoryName ||
                                      "General Hazard"}
                                  </td>
                                  <td className="text-center">
                                    {report.isImmediateDanger
                                      ? "Immediate"
                                      : "Normal"}
                                  </td>
                                  <td className="text-center">
                                    {report.approvalStatus ? (
                                      <span
                                        className={`status-badge ${report.approvalStatus.toLowerCase()}`}
                                      >
                                        {report.approvalStatus}
                                      </span>
                                    ) : (
                                      <span className="text-muted">N/A</span>
                                    )}
                                  </td>
                                  <td className="text-center">
                                    {report.approvalStatus === "Pending" &&
                                      report.approverRoleId ===
                                      currentUserRoleId && (
                                        <div
                                          className="approval-actions"
                                          style={{
                                            display: "flex",
                                            gap: "8px",
                                            justifyContent: "center",
                                          }}
                                        >
                                          <button
                                            title="Approve"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              setApprovalModal({
                                                isOpen: true,
                                                hazardId: report.hazardId,
                                                status: "Approved",
                                                remarks: "",
                                              });
                                            }}
                                            style={{
                                              display: "inline-flex",
                                              alignItems: "center",
                                              gap: "4px",
                                              background: SEMANTIC_SUCCESS_MID,
                                              color: "white",
                                              border: "none",
                                              borderRadius: "6px",
                                              padding: "6px 12px",
                                              cursor: "pointer",
                                              fontSize: "13px",
                                              fontWeight: "500",
                                              transition: "all 0.2s"
                                            }}
                                          >
                                            <HiOutlineCheckCircle size={16} /> Approve
                                          </button>
                                          <button
                                            title="Reject"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              setApprovalModal({
                                                isOpen: true,
                                                hazardId: report.hazardId,
                                                status: "Rejected",
                                                remarks: "",
                                              });
                                            }}
                                            style={{
                                              display: "inline-flex",
                                              alignItems: "center",
                                              gap: "4px",
                                              background: "#ef4444",
                                              color: "white",
                                              border: "none",
                                              borderRadius: "6px",
                                              padding: "6px 12px",
                                              cursor: "pointer",
                                              fontSize: "13px",
                                              fontWeight: "500",
                                              transition: "all 0.2s"
                                            }}
                                          >
                                            <HiOutlineXMark size={16} /> Reject
                                          </button>
                                        </div>
                                      )}
                                  </td>
                                  <td className="text-center">
                                    <div className="action-buttons">
                                      <div
                                        className="btn-icon-edit cursor-pointer"
                                        title="Edit"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setEditingHazardId(report.hazardId);
                                          setIsFormOpen(true);
                                        }}
                                      >
                                        <HiOutlinePencil size={18} />
                                      </div>
                                      <div
                                        className="btn-icon-edit cursor-pointer"
                                        title="View"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setEditingHazardId(report.hazardId);
                                          setIsFormOpen(true);
                                        }}
                                      >
                                        <HiOutlineEye size={18} />
                                      </div>

                                      <div
                                        className="btn-icon-delete cursor-pointer"
                                        title="Delete"
                                        onClick={(e) =>
                                          handleDelete(e, report.hazardId)
                                        }
                                      >
                                        <HiOutlineTrash size={18} />
                                      </div>
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ))
        )}
      </div>

      <HazardReportForm
        isOpen={isFormOpen}
        onClose={() => {
          setIsFormOpen(false);
          setEditingHazardId(null);
          setAiPrefillData(null);
          setAiPrefillFiles([]);
          fetchReports(); // reload list when form is closed
        }}
        reportId={editingHazardId}
        aiPrefillObject={aiPrefillData}
        aiPrefillFiles={aiPrefillFiles}
      />

      <AIImageFeature
        isOpen={isAIFeatureOpen}
        onClose={() => setIsAIFeatureOpen(false)}
        onReportHazard={(aiData, files) => {
          setIsAIFeatureOpen(false);
          setEditingHazardId(null);
          setAiPrefillData(aiData);
          setAiPrefillFiles(files);
          setIsFormOpen(true);
        }}
      />

      {/* Approval Modal */}
      <SharedModal
        isOpen={approvalModal.isOpen}
        onClose={() =>
          setApprovalModal({
            isOpen: false,
            hazardId: null,
            status: "",
            remarks: "",
          })
        }
        title={`${approvalModal.status} Report`}
        footer={
          <>
            <button
              className="btn btn-secondary"
              onClick={() =>
                setApprovalModal({
                  isOpen: false,
                  hazardId: null,
                  status: "",
                  remarks: "",
                })
              }
            >
              Cancel
            </button>
            <button
              className="btn btn-primary"
              onClick={submitApproval}
              style={{
                background:
                  approvalModal.status === "Approved"
                    ? SEMANTIC_SUCCESS_MID
                    : "#ef4444",
              }}
            >
              Submit {approvalModal.status}
            </button>
          </>
        }
      >
        <div className="shared-modal-body">
          <div className="form-group" style={{ marginBottom: "16px" }}>
            <label
              style={{
                display: "block",
                marginBottom: "8px",
                fontWeight: "600",
              }}
            >
              Remarks (Required)
            </label>
            <textarea
              value={approvalModal.remarks}
              onChange={(e) =>
                setApprovalModal((prev) => ({
                  ...prev,
                  remarks: e.target.value,
                }))
              }
              placeholder={`Please provide your remarks for ${approvalModal.status.toLowerCase()} this report...`}
              rows={4}
              style={{
                width: "100%",
                padding: "8px",
                borderRadius: "4px",
                border: "1px solid #ccc",
              }}
            />
          </div>
        </div>
      </SharedModal>

      {/* Delete/Escalate Confirmation Modal */}
      <SharedModal
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ ...deleteModal, isOpen: false })}
        title={deleteModal.title}
        footer={
          <>
            <button
              className="btn btn-secondary"
              onClick={() => setDeleteModal({ ...deleteModal, isOpen: false })}
            >
              Cancel
            </button>
            <button
              className={
                deleteModal.type === "delete"
                  ? "btn btn-danger"
                  : "btn btn-success"
              }
              onClick={
                deleteModal.type === "delete" ? confirmDelete : confirmEscalate
              }
            >
              {deleteModal.type === "delete" ? "Delete" : "Escalate"}
            </button>
          </>
        }
      >
        <div className="shared-modal-body">
          <p>{deleteModal.message}</p>
        </div>
      </SharedModal>
    </div>
  );
};

const AIImageFeature = ({ isOpen, onClose, onReportHazard }) => {
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [previewUrls, setPreviewUrls] = useState([]);
  const [isScanning, setIsScanning] = useState(false);
  const [scanComplete, setScanComplete] = useState(false);
  const [scanProgress, setScanProgress] = useState(0);
  const [scanStep, setScanStep] = useState("");
  const fileInputRef = React.useRef(null);
  const stepIntervalRef = React.useRef(null);
  const progressIntervalRef = React.useRef(null);
  const transitionTimeoutRef = React.useRef(null);

  const clearAllTimers = () => {
    if (stepIntervalRef.current) clearInterval(stepIntervalRef.current);
    if (progressIntervalRef.current) clearInterval(progressIntervalRef.current);
    if (transitionTimeoutRef.current) clearTimeout(transitionTimeoutRef.current);
    stepIntervalRef.current = null;
    progressIntervalRef.current = null;
    transitionTimeoutRef.current = null;
  };

  React.useEffect(() => {
    return () => clearAllTimers();
  }, []);

  if (!isOpen) return null;

  const resetState = () => {
    clearAllTimers();
    setSelectedFiles([]);
    if (previewUrls.length > 0) {
      previewUrls.forEach((url) => URL.revokeObjectURL(url));
    }
    setPreviewUrls([]);
    setIsScanning(false);
    setScanComplete(false);
    setScanProgress(0);
    setScanStep("");
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const processImages = (files) => {
    if (!files || files.length === 0) return;

    const validFiles = Array.from(files).filter((f) =>
      f.type.startsWith("image/"),
    );
    if (validFiles.length === 0) {
      toast.error("Please upload image files only");
      return;
    }

    setSelectedFiles(validFiles);

    // Create preview URLs
    const urls = validFiles.map((file) => URL.createObjectURL(file));
    setPreviewUrls(urls);

    startScan(validFiles);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      processImages(e.dataTransfer.files);
    }
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      processImages(e.target.files);
    }
  };

  const handleContainerClick = () => {
    if (selectedFiles.length === 0) {
      fileInputRef.current.click();
    }
  };

  const startScan = (files) => {
    setIsScanning(true);
    setScanComplete(false);
    setScanProgress(0);

    const steps = [
      "Initializing AI Vision Core...",
      "Analyzing Structural Elements...",
      "Detecting PPE Compliance...",
      "Mapping Hazard Zones...",
      "Finalizing Computed Probabilities...",
    ];

    let currentStep = 0;
    setScanStep(steps[currentStep]);

    const stepInterval = setInterval(() => {
      currentStep++;
      if (currentStep < steps.length) {
        setScanStep(steps[currentStep]);
      }
    }, 400);
    stepIntervalRef.current = stepInterval;

    // Run actual API call asynchronously with the FIRST image
    let apiData = null;
    let apiError = false;
    let isApiDone = false;
    const fileToScan = files[0];

    aiService
      .analyzeHazardImage(fileToScan)
      .then((res) => {
        apiData = res;
        isApiDone = true;
      })
      .catch((err) => {
        console.error("AI Analysis Failed", err);
        apiError = true;
        isApiDone = true;
      });

    let progress = 0;
    const progressInterval = setInterval(() => {
      // Don't reach 100% until API is done
      if (isApiDone || progress < 85) {
        progress += Math.floor(Math.random() * 8) + 4;
      }

      if (progress >= 100) progress = 100;
      setScanProgress(progress);

      // Once visually 100% AND API is done, proceed
      if (progress >= 100 && isApiDone) {
        clearInterval(progressInterval);
        clearInterval(stepInterval);
        stepIntervalRef.current = null;
        progressIntervalRef.current = null;

        setIsScanning(false);
        setScanComplete(true);

        const finalResult =
          !apiError && apiData
            ? apiData
            : {
              hazardDescription:
                "Image scanned. Please manually review the findings.",
              suggestedAction: "Evaluate and isolate the area.",
              suggestedCategory: "General Safety",
              riskLevel: "medium",
            };

        transitionTimeoutRef.current = setTimeout(() => {
          if (onReportHazard) {
            onReportHazard(finalResult, files);
          }
          transitionTimeoutRef.current = null;
        }, 500); // Small delay to read "Analysis Complete"
      }
    }, 100);
    progressIntervalRef.current = progressInterval;
  };

  return (
    <SharedModal
      isOpen={isOpen}
      onClose={handleClose}
      title="AI Vision Interface"
      customWidth="780px"
      footer={
        <div className="ai-modal-footer" style={{ borderTop: 'none', padding: 0 }}>
          {previewUrls.length > 0 && !isScanning && !scanComplete && (
            <button className="btn btn-secondary" onClick={resetState}>
              Upload Different Images
            </button>
          )}
          <button className="btn btn-secondary" onClick={handleClose}>
            Cancel
          </button>
        </div>
      }
    >
      <div className="shared-modal-body">
        {previewUrls.length === 0 ? (
          <div
            className={`ai-uploader-container ${previewUrls.length === 0 ? "pulse-border" : ""}`}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            onClick={handleContainerClick}
          >
            <div className="ai-upload-icon-wrapper">
              <HiOutlineArrowUpTray className="ai-uploader-icon" />
            </div>
            <div className="ai-uploader-text">
              <h3>Drag & Drop Images or Browse</h3>
              <p>Select one or multiple images for AI scanning (Max 15MB)</p>
            </div>
            <input
              type="file"
              className="ai-file-input"
              ref={fileInputRef}
              onChange={handleFileChange}
              accept="image/*"
              multiple
              style={{ display: 'none' }}
            />
          </div>
        ) : (
          <div className="ai-scanner-view">
            <div className="ai-image-preview-container" style={{ position: 'relative' }}>
              <img
                src={previewUrls[0]}
                alt="Uploaded for analysis"
                className="ai-preview-image"
                style={{ width: '100%', height: 'auto', borderRadius: '12px' }}
              />

              {previewUrls.length > 1 && (
                <div
                  style={{
                    display: "flex",
                    gap: "8px",
                    position: "absolute",
                    bottom: "16px",
                    left: "16px",
                    zIndex: 10,
                  }}
                >
                  {previewUrls.map((url, idx) => (
                    <img
                      key={idx}
                      src={url}
                      alt={`Preview ${idx}`}
                      style={{
                        width: "40px",
                        height: "40px",
                        objectFit: "cover",
                        borderRadius: "4px",
                        border:
                          idx === 0
                            ? `2px solid ${SEMANTIC_SUCCESS_MID}`
                            : "1px solid rgba(255,255,255,0.8)",
                        boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                      }}
                    />
                  ))}
                </div>
              )}

              {isScanning && (
                <div className="ai-scan-layer" style={{ position: 'absolute', inset: 0, background: 'rgba(15, 23, 42, 0.7)', borderRadius: '12px', zIndex: 10, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '40px' }}>
                  <div className="ai-scanning-overlay professional" style={{ color: 'white', textAlign: 'center' }}>
                    <div className="ai-progress-circle" style={{ fontSize: '2.5rem', fontWeight: 800, marginBottom: '15px' }}>
                      <span>{scanProgress}%</span>
                    </div>
                    <span className="ai-scan-step-text" style={{ fontSize: '1.1rem', opacity: 0.9 }}>{scanStep}</span>
                    <div className="ai-progress-bar-container" style={{ width: '280px', height: '8px', background: 'rgba(255,255,255,0.2)', borderRadius: '4px', marginTop: '20px', overflow: 'hidden' }}>
                      <div
                        className="ai-progress-bar-fill"
                        style={{ width: `${scanProgress}%`, height: '100%', background: '#6366f1' }}
                      ></div>
                    </div>
                  </div>
                </div>
              )}

              {scanComplete && (
                <div className="ai-scan-layer" style={{ position: 'absolute', inset: 0, background: 'rgba(16, 185, 129, 0.9)', borderRadius: '12px', zIndex: 11, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '40px' }}>
                  <div className="ai-scanning-overlay professional" style={{ color: 'white', textAlign: 'center' }}>
                    <HiOutlineCheckCircle size={64} style={{ marginBottom: '20px' }} />
                    <span
                      style={{
                        fontSize: "1.5rem",
                        fontWeight: "800",
                        display: 'block'
                      }}
                    >
                      Analysis Complete
                    </span>
                    <p
                      style={{
                        opacity: 0.9,
                        fontSize: "1rem",
                        marginTop: '5px'
                      }}
                    >
                      Preparing hazard report…
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </SharedModal>
  );
};

export default HazardReportList;
